/**
 * GenerationSettingsPanel — right editor of the Generation shell (Block 2026-08-02).
 *
 * Spec ref: PRODUCT_SPEC_WORKFLOW_MATCHING_TRANSLATION_GENERATION_V1.md §16.5–16.8.
 *
 * Renders the settings controls for the content type selected in the left rail.
 * Three control variants:
 *   - text  : prompt / additional prompt / length / style / input notes /
 *             negative prompt / temperature / repetition (тошнота) /
 *             academicity / output format (plain / HTML / Markdown).
 *   - seo   : on/off (enabled) / FAQ count / source-basis hint / length /
 *             temperature.
 *   - custom: output name / prompt / length / temperature / output format.
 *
 * Honesty (ТЗ Part D): every visible control is REALLY wired — its value lives
 * in `contentConfigs[<type>]` and is sent to the backend in
 * `content_type_configs` on every pipeline start, then persisted into
 * pipeline_config. For types whose runtime is not registered yet, a banner
 * states that settings are saved and will apply when the runtime lands; the
 * controls stay editable (they are not dead — they persist).
 */
import { useTranslation } from 'react-i18next';
import { Info } from 'lucide-react';

import { useGenerationStore, type ContentConfig, type OutputFormat, type SourceBasis } from '@/store/generationStore';
import { CONTENT_TYPE_MAP } from '@/components/generation/contentTypes';
import { cn } from '@/lib/utils';

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <div className="flex items-center gap-1.5 mb-1">
        <span className="text-xs text-surface-400">{label}</span>
        {hint && (
          <span className="text-[10px] text-surface-500" title={hint}>
            ⓘ
          </span>
        )}
      </div>
      {children}
    </label>
  );
}

const inputCls =
  'w-full bg-field border border-surface-700 rounded-lg px-3 py-2 text-sm text-surface-100 focus:outline-none focus:border-primary-500/50';

export function GenerationSettingsPanel() {
  const { t } = useTranslation();
  const selectedKey = useGenerationStore((s) => s.selectedContentType);
  const contentConfigs = useGenerationStore((s) => s.contentConfigs);
  const update = useGenerationStore((s) => s.updateContentConfig);
  const toggleEnabled = useGenerationStore((s) => s.toggleContentEnabled);

  const def = CONTENT_TYPE_MAP[selectedKey];
  if (!def) {
    return (
      <div className="card text-sm text-surface-500">
        {t('generation.settings.noType', { defaultValue: 'Select a content type on the left.' })}
      </div>
    );
  }
  const cfg: ContentConfig = contentConfigs[selectedKey] ?? { ...def.defaultConfig };
  const set = (patch: Partial<ContentConfig>) => update(selectedKey, patch);

  return (
    <div className="card flex flex-col">
      {/* Header: type name + enabled + runtime badge */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold">
            {t(def.labelKey, { defaultValue: def.defaultLabel })}
          </h3>
          {!def.runtimeEnabled && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-surface-700/50 text-surface-400">
              {t('generation.types.runtimeSoon', { defaultValue: 'soon' })}
            </span>
          )}
        </div>
        <label className="inline-flex items-center gap-2 text-xs text-surface-400 cursor-pointer">
          <input
            type="checkbox"
            checked={cfg.enabled}
            onChange={() => toggleEnabled(selectedKey)}
            className="accent-primary-500"
          />
          {t('generation.settings.enabled', { defaultValue: 'Enabled' })}
        </label>
      </div>

      {!def.runtimeEnabled && (
        <div className="flex items-start gap-2 mb-4 rounded-lg border border-surface-700/60 bg-surface-800/30 px-3 py-2 text-xs text-surface-400">
          <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <span>
            {t('generation.settings.runtimeSoonNotice', {
              defaultValue:
                'Runtime for this type is coming soon. Your settings are saved and will be applied automatically when the runtime lands.',
            })}
          </span>
        </div>
      )}

      {/* ---- Variant: text ---- */}
      {def.settingsVariant === 'text' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label={t('generation.settings.outputColumnName', { defaultValue: 'Output column name' })}>
              <input
                className={inputCls}
                value={cfg.outputColumnName ?? ''}
                onChange={(e) => set({ outputColumnName: e.target.value })}
                placeholder="description"
              />
            </Field>
            <Field
              label={t('generation.settings.length', { defaultValue: 'Target length (chars without spaces)' })}
              hint={t('generation.settings.lengthHint', { defaultValue: 'Up to 15% tolerance for natural completion' })}
            >
              <input
                type="number"
                min={50}
                max={8000}
                className={inputCls}
                value={cfg.length ?? 0}
                onChange={(e) => set({ length: Number(e.target.value) || 0 })}
              />
            </Field>
          </div>

          <Field label={t('generation.settings.prompt', { defaultValue: 'User context / prompt' })}>
            <textarea
              className={cn(inputCls, 'min-h-[160px] resize-y')}
              value={cfg.prompt ?? ''}
              onChange={(e) => set({ prompt: e.target.value })}
              placeholder={t('generation.settings.promptPlaceholder', {
                defaultValue: 'Store name, delivery terms, tone, anything not in the selected columns…',
              })}
            />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label={t('generation.settings.style', { defaultValue: 'Style' })}>
              <input
                className={inputCls}
                value={cfg.style ?? ''}
                onChange={(e) => set({ style: e.target.value })}
                placeholder={t('generation.settings.stylePlaceholder', { defaultValue: 'e.g. concise, selling, neutral' })}
              />
            </Field>
            <Field label={t('generation.settings.outputFormat', { defaultValue: 'Output format' })}>
              <select
                className={inputCls}
                value={cfg.outputFormat ?? 'plain'}
                onChange={(e) => set({ outputFormat: e.target.value as OutputFormat })}
              >
                <option value="plain">{t('generation.settings.fmtPlain', { defaultValue: 'Plain text' })}</option>
                <option value="html">HTML</option>
                <option value="markdown">Markdown</option>
              </select>
            </Field>
          </div>

          <Field label={t('generation.settings.inputNotes', { defaultValue: 'Input notes' })}>
            <textarea
              className={cn(inputCls, 'min-h-[80px] resize-y')}
              value={cfg.inputNotes ?? ''}
              onChange={(e) => set({ inputNotes: e.target.value })}
              placeholder={t('generation.settings.inputNotesPlaceholder', { defaultValue: 'Extra constraints for the AI input' })}
            />
          </Field>

          <Field label={t('generation.settings.negativePrompt', { defaultValue: 'Negative prompt' })}>
            <textarea
              className={cn(inputCls, 'min-h-[70px] resize-y')}
              value={cfg.negativePrompt ?? ''}
              onChange={(e) => set({ negativePrompt: e.target.value })}
              placeholder={t('generation.settings.negativePlaceholder', { defaultValue: 'What to avoid (claims, words, tone…)' })}
            />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label={t('generation.settings.temperature', { defaultValue: 'Temperature' })}>
              <input
                type="range"
                min={0}
                max={2}
                step={0.05}
                className="w-full accent-primary-500"
                value={cfg.temperature ?? 0.7}
                onChange={(e) => set({ temperature: Number(e.target.value) })}
              />
              <div className="text-[10px] text-surface-500 mt-0.5">{cfg.temperature?.toFixed(2)}</div>
            </Field>
            <Field
              label={t('generation.settings.repetition', { defaultValue: 'Repetition / тошнота' })}
              hint={t('generation.settings.repetitionHint', { defaultValue: 'Lower = less repetition' })}
            >
              <input
                type="range"
                min={0}
                max={1}
                step={0.05}
                className="w-full accent-primary-500"
                value={cfg.repetition ?? 0}
                onChange={(e) => set({ repetition: Number(e.target.value) })}
              />
              <div className="text-[10px] text-surface-500 mt-0.5">{cfg.repetition?.toFixed(2)}</div>
            </Field>
            <Field
              label={t('generation.settings.academicity', { defaultValue: 'Factual / academic precision' })}
              hint={t('generation.settings.academicityHint', { defaultValue: 'Higher = stricter factual tone' })}
            >
              <input
                type="range"
                min={0}
                max={1}
                step={0.05}
                className="w-full accent-primary-500"
                value={cfg.academicity ?? 0.5}
                onChange={(e) => set({ academicity: Number(e.target.value) })}
              />
              <div className="text-[10px] text-surface-500 mt-0.5">{cfg.academicity?.toFixed(2)}</div>
            </Field>
          </div>
        </div>
      )}

      {/* ---- Variant: seo ---- */}
      {def.settingsVariant === 'seo' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field
              label={t('generation.settings.length', { defaultValue: 'Target length (chars)' })}
            >
              <input
                type="number"
                min={20}
                max={400}
                className={inputCls}
                value={cfg.length ?? 0}
                onChange={(e) => set({ length: Number(e.target.value) || 0 })}
              />
            </Field>
            <Field
              label={t('generation.settings.sourceBasis', { defaultValue: 'Source basis' })}
              hint={t('generation.settings.sourceBasisHint', {
                defaultValue: 'What the AI should base the output on',
              })}
            >
              <select
                className={inputCls}
                value={cfg.sourceBasis ?? 'facts'}
                onChange={(e) => set({ sourceBasis: e.target.value as SourceBasis })}
              >
                <option value="generated">{t('generation.settings.basisGenerated', { defaultValue: 'Generated text' })}</option>
                <option value="existing">{t('generation.settings.basisExisting', { defaultValue: 'Existing text' })}</option>
                <option value="facts">{t('generation.settings.basisFacts', { defaultValue: 'Row facts' })}</option>
              </select>
            </Field>
          </div>

          {selectedKey === 'faq' && (
            <Field
              label={t('generation.settings.faqCount', { defaultValue: 'FAQ count' })}
              hint={t('generation.settings.faqCountHint', { defaultValue: 'Number of question-answer pairs' })}
            >
              <input
                type="number"
                min={1}
                max={20}
                className={inputCls}
                value={cfg.faqCount ?? 5}
                onChange={(e) => set({ faqCount: Number(e.target.value) || 5 })}
              />
            </Field>
          )}

          <Field label={t('generation.settings.temperature', { defaultValue: 'Temperature' })}>
            <input
              type="range"
              min={0}
              max={2}
              step={0.05}
              className="w-full accent-primary-500"
              value={cfg.temperature ?? 0.5}
              onChange={(e) => set({ temperature: Number(e.target.value) })}
            />
            <div className="text-[10px] text-surface-500 mt-0.5">{cfg.temperature?.toFixed(2)}</div>
          </Field>
        </div>
      )}

      {/* ---- Variant: custom ---- */}
      {def.settingsVariant === 'custom' && (
        <div className="space-y-4">
          <Field label={t('generation.settings.outputColumnName', { defaultValue: 'Output field name' })}>
            <input
              className={inputCls}
              value={cfg.customName ?? ''}
              onChange={(e) => set({ customName: e.target.value })}
              placeholder="custom_field"
            />
          </Field>
          <Field
            label={t('generation.settings.prompt', { defaultValue: 'Prompt' })}
            hint={t('generation.settings.customPromptHint', {
              defaultValue: 'Prepare a high-quality prompt (an external AI can help). No in-app assistant for custom fields in v1.',
            })}
          >
            <textarea
              className={cn(inputCls, 'min-h-[200px] resize-y')}
              value={cfg.customPrompt ?? ''}
              onChange={(e) => set({ customPrompt: e.target.value })}
              placeholder={t('generation.settings.customPromptPlaceholder', {
                defaultValue: 'Describe exactly what to generate for each row…',
              })}
            />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label={t('generation.settings.length', { defaultValue: 'Target length (chars)' })}>
              <input
                type="number"
                min={50}
                max={8000}
                className={inputCls}
                value={cfg.length ?? 0}
                onChange={(e) => set({ length: Number(e.target.value) || 0 })}
              />
            </Field>
            <Field label={t('generation.settings.outputFormat', { defaultValue: 'Output format' })}>
              <select
                className={inputCls}
                value={cfg.outputFormat ?? 'plain'}
                onChange={(e) => set({ outputFormat: e.target.value as OutputFormat })}
              >
                <option value="plain">{t('generation.settings.fmtPlain', { defaultValue: 'Plain text' })}</option>
                <option value="html">HTML</option>
                <option value="markdown">Markdown</option>
              </select>
            </Field>
          </div>
          <Field label={t('generation.settings.temperature', { defaultValue: 'Temperature' })}>
            <input
              type="range"
              min={0}
              max={2}
              step={0.05}
              className="w-full accent-primary-500"
              value={cfg.temperature ?? 0.7}
              onChange={(e) => set({ temperature: Number(e.target.value) })}
            />
            <div className="text-[10px] text-surface-500 mt-0.5">{cfg.temperature?.toFixed(2)}</div>
          </Field>
        </div>
      )}
    </div>
  );
}